import {NavigationOptions, RouteHandler, Router} from '@multipass-system';
import {Logger} from 'esp-js';

const _log = Logger.create('DefaultRouter');

interface RouteEntry {
    pattern: string;
    regex: RegExp;
    handler: RouteHandler;
}

// ':param' segments become named capture groups; static segments are regex-escaped.
const compilePattern = (pattern: string): RegExp => {
    const source = pattern
        .split('/')
        .map(segment => segment.startsWith(':')
            ? `(?<${segment.slice(1)}>[^/]+)`
            : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('/');
    return new RegExp(`^${source}$`);
};

export class DefaultRouter implements Router {
    #routes: RouteEntry[] = [];
    #started = false;
    readonly #onPopState = (event: PopStateEvent) => {
        this.#activate(event.state);
    };

    get currentPath(): string {
        return window.location.pathname;
    }

    get isAtRoot(): boolean {
        return this.currentPath === '/';
    }

    start(): void {
        if (this.#started) {
            return;
        }
        this.#started = true;
        window.addEventListener('popstate', this.#onPopState);
        this.#activate(window.history.state);
    }

    navigate(path: string, options?: NavigationOptions): void {
        const state = options?.state ?? null;
        if (options?.replace) {
            window.history.replaceState(state, '', path);
        } else {
            window.history.pushState(state, '', path);
        }
        this.#activate(state);
    }

    register(path: string, handler: RouteHandler): () => void {
        const entry: RouteEntry = {pattern: path, regex: compilePattern(path), handler};
        this.#routes.push(entry);
        return () => {
            const index = this.#routes.indexOf(entry);
            if (index >= 0) {
                this.#routes.splice(index, 1);
            }
        };
    }

    dispose(): void {
        window.removeEventListener('popstate', this.#onPopState);
        this.#routes = [];
        this.#started = false;
    }

    #activate(state: unknown): void {
        const path = window.location.pathname;
        for (const route of this.#routes) {
            const match = route.regex.exec(path);
            if (!match) {
                continue;
            }
            const params: Record<string, string> = {};
            for (const [name, value] of Object.entries(match.groups ?? {})) {
                params[name] = decodeURIComponent(value);
            }
            route.handler({
                pathMatched: route.pattern,
                params,
                searchParams: new URLSearchParams(window.location.search),
                state,
            });
            return;
        }
        _log.warn(`No route matched path '${path}'`);
    }
}
